import { useEffect, useRef, useState } from 'react';

import type { BridgeQuoteResponse } from '@/lib/api/speed';
import { getErrorMessage } from '@/lib/errors';

import { normalizeBridgeQuote } from '../services/bridgeFees';
import type { BridgeQuote } from '../services/bridgeFees';

/**
 * The amount-entry pre-quote: what this exact amount would cost and yield,
 * asked while the user is still typing.
 *
 * Why it gates the button rather than merely decorating the form: the bridge
 * behind a transfer is chosen at quote time, and the deBridge failover costs
 * dollars where Relay costs cents and carries a higher floor. Letting someone
 * press Withdraw against the flat estimate means they commit to a price nobody
 * has actually quoted. So until a successful quote for the CURRENT inputs is
 * in hand, the button stays shut.
 *
 * Everything about the timing is there to keep that honest:
 * - one debounce window, so a number being typed does not quote per keystroke;
 * - the answer is stamped with the inputs it was asked for, so a slow reply for
 *   an older amount can never unlock the button for a newer one;
 * - the in-flight request is aborted when the inputs change.
 */

/** Long enough to skip the digits of a number being typed, short enough not to feel stuck. */
export const BRIDGE_QUOTE_DEBOUNCE_MS = 600;

/**
 * Consecutive transport failures re-tried on their own. Past this the surface
 * keeps its plain retry line and waits for the user to change something, rather
 * than hammering a backend that is plainly down.
 */
const MAX_TRANSPORT_RETRIES = 3;

const TRANSPORT_RETRY_TEXT =
    'Could not price this right now. Retrying, or adjust the amount to try again.';

export interface BridgeQuoteState {
    /**
     * A successful quote for the CURRENT inputs is held. The one thing callers
     * gate a button on.
     */
    ready: boolean;
    /** The quote, once `ready`. Null at every other moment. */
    quote: BridgeQuote | null;
    /**
     * Why it cannot proceed, in plain language: the backend's own `reason` for
     * a refused amount, or this module's retry line for a transport failure.
     */
    reason: string | null;
    /**
     * The backend's machine name for a refusal, when it gave one, so a caller
     * can offer a specific way out instead of only echoing the sentence.
     */
    reasonCode: string | null;
    /** A quote is being waited for (debounce counting or request in flight). */
    loading: boolean;
}

const IDLE: BridgeQuoteState = {
    ready: false,
    quote: null,
    reason: null,
    reasonCode: null,
    loading: false,
};

interface Internal extends BridgeQuoteState {
    /** The inputs this state describes; null means "nothing to quote". */
    key: string | null;
}

export interface UseBridgeQuoteParams {
    /**
     * Everything the quote depends on, as one string. Null disables quoting
     * entirely, which is also what a form with no quoting wired in passes, so
     * that form behaves exactly as it did before.
     */
    key: string | null;
    /** Asks the backend. Read through a ref, so its identity may change freely. */
    fetch: (signal: AbortSignal) => Promise<BridgeQuoteResponse>;
    debounceMs?: number;
}

/**
 * Debounced, self-cancelling pre-quote for one set of inputs.
 *
 * Returns {@link IDLE} while `key` is null, so a caller that cannot quote (no
 * transport wired, an amount that is not usable yet, a route that does not
 * bridge) is simply not gated.
 */
export function useBridgeQuote({
    key,
    fetch,
    debounceMs = BRIDGE_QUOTE_DEBOUNCE_MS,
}: UseBridgeQuoteParams): BridgeQuoteState {
    // The fetcher is a fresh closure on every render (it closes over the amount
    // and the API client). Depending on it would re-arm the debounce on every
    // render and quote forever, so it is read through a ref and `key` alone
    // decides when to ask again.
    const fetchRef = useRef(fetch);
    useEffect(() => {
        fetchRef.current = fetch;
    });

    const [state, setState] = useState<Internal>({ ...IDLE, key: null });

    // Retrying a transport failure is a state change of its own, so it needs a
    // dependency the effect can see; `key` must NOT change for a retry or the
    // failure count would reset and the retry would never be bounded.
    const [attempt, setAttempt] = useState(0);
    // Counted outside React state on purpose: an updater must stay pure, and
    // this is read and written in the same breath as scheduling the retry.
    const failuresRef = useRef<{ key: string | null; count: number }>({
        key: null,
        count: 0,
    });

    useEffect(() => {
        if (!key) {
            failuresRef.current = { key: null, count: 0 };
            setState((prev) =>
                prev.key === null ? prev : { ...IDLE, key: null },
            );
            return;
        }

        // A quote in hand describes the inputs it was asked for. New inputs mean
        // the old answer no longer applies, so the button shuts immediately
        // rather than staying open on a stale price for the debounce window.
        setState((prev) =>
            prev.key === key
                ? { ...prev, loading: true }
                : { ...IDLE, key, loading: true },
        );

        const controller = new AbortController();
        let cancelled = false;

        const timer = setTimeout(() => {
            void (async () => {
                try {
                    const answer = await fetchRef.current(controller.signal);
                    if (cancelled) return;
                    const quote = normalizeBridgeQuote(answer);
                    failuresRef.current = { key, count: 0 };
                    setState({
                        key,
                        loading: false,
                        ready: quote.ok,
                        quote: quote.ok ? quote : null,
                        reason: quote.ok
                            ? null
                            : (quote.reason ??
                              'This amount cannot be sent right now.'),
                        reasonCode: quote.ok ? null : quote.code,
                    });
                } catch (error) {
                    if (cancelled || controller.signal.aborted) return;
                    // never surfaced raw: the user gets the retry line, the
                    // console gets the real failure.
                    console.warn(
                        '[BridgeQuote] pre-quote failed',
                        getErrorMessage(error),
                    );
                    const failures =
                        (failuresRef.current.key === key
                            ? failuresRef.current.count
                            : 0) + 1;
                    failuresRef.current = { key, count: failures };
                    const retrying = failures <= MAX_TRANSPORT_RETRIES;
                    setState({
                        key,
                        loading: retrying,
                        ready: false,
                        quote: null,
                        reason: TRANSPORT_RETRY_TEXT,
                        reasonCode: null,
                    });
                    if (retrying) {
                        setAttempt((value) => value + 1);
                    }
                }
            })();
        }, debounceMs);

        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timer);
        };
    }, [attempt, debounceMs, key]);

    return {
        ready: state.key === key && state.ready,
        quote: state.key === key ? state.quote : null,
        reason: state.key === key ? state.reason : null,
        reasonCode: state.key === key ? state.reasonCode : null,
        loading: state.key === key ? state.loading : !!key,
    };
}
